/**
 * Shared TanStack Query client.
 *
 * Defaults tuned for an auth-heavy app:
 * - No retry on 4xx (auth failures, validation errors are user-actionable)
 * - Retry once on 5xx and network errors
 * - 30s staleTime on /me — we don't need to refetch on every mount
 * - refetchOnWindowFocus only for stale data
 */

import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { ApiError } from "./api";

/**
 * E-401 (PO 02-Sep): a session can now die MID-USE (30-min idle timeout,
 * continuous IP-allowlist enforcement), not only at the edges. Without a
 * global reaction, data queries fail with 401 while the cached UI keeps
 * rendering a logged-in ghost. Reaction: any 401 from a data query or
 * mutation invalidates the "auth" domain; the /me probe refetches, gets
 * its own 401, deriveStatus flips to "unauthenticated" and the existing
 * route guards redirect to /login. The auth domain itself is EXCLUDED
 * (the /me probe 401s legitimately for guests) so a logged-out visitor
 * can never loop. 403 stays untouched (mfa_required has its own flow).
 */
function onAuthError(error: unknown, scopeKey: unknown): void {
  if (!(error instanceof ApiError) || error.status !== 401) return;
  if (Array.isArray(scopeKey) && scopeKey[0] === "auth") return;
  queryClient.invalidateQueries({ queryKey: ["auth"] });
}

export const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => onAuthError(error, query.queryKey),
  }),
  mutationCache: new MutationCache({
    onError: (error, _v, _c, mutation) => onAuthError(error, mutation.options.mutationKey),
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000, // 30s — most auth/profile data doesn't change often
      gcTime: 5 * 60_000,
      retry: (failureCount, error) => {
        if (error instanceof ApiError) {
          // 4xx errors are user-actionable, don't retry
          if (error.status >= 400 && error.status < 500) return false;
          // 5xx or network errors: one retry
          return failureCount < 1;
        }
        return failureCount < 1;
      },
      refetchOnWindowFocus: "always",
    },
    mutations: {
      retry: false,
    },
  },
});
